import { Close, SupportRefBtnContext, useIdentityTimer } from '@skezi/ui';
import IdentityConfirmationModal from '@skezi/ui/src/components/identityConfirmation/IdentityConfirmationModal';
import { Fragment, useContext, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../src/auth/useAuth';
import { BasicButton } from '../../../components/common/Button';
import { ToastQueueRefContext } from '../../../components/common/Toast';
import UsersService from '../../../services/users.service';
import AuthSettingsItemHeader from '../authSettings/AuthSettingsItemHeader';
import YouHaveKidsModal from './YouHaveKidsModal';
import { informationAboutDeletion } from './data';

const DashboardDeleteAccount = () => {
  const { user, logout } = useAuth();
  const supportBtnRef = useContext(SupportRefBtnContext);
  const navigate: NavigateFunction = useNavigate();
  const toastQueueRef = useContext(ToastQueueRefContext);
  const usersService = new UsersService();

  const userApps: ('SKEZIA' | 'SKEMEET')[] = [];
  if (user?.skezia) userApps.push('SKEZIA');
  if (user?.skemeet) userApps.push('SKEMEET');

  const [kidsModalOpen, setKidsModalOpen] = useState<boolean>(false);
  const [identityConfirmationModalOpen, setIdentityConfirmationModal] = useState<boolean>(false);
  const { timeLimit, startTimer } = useIdentityTimer(60);

  const requestConfirmIdentity = async () => {
    try {
      await usersService.requestToConfirmIdentity(false);
    } catch (error) {
      toastQueueRef?.current?.addToast({ type: 'ERROR', description: (error as Error)?.message, timer: 2000 });
    }
  };

  const reSendCode = async () => {
    try {
      await usersService.requestToConfirmIdentity(true);
      toastQueueRef?.current?.addToast({ type: 'SUCCESS', description: 'Code renvoyé avec succès', timer: 5000 });
      startTimer();
    } catch (error) {
      toastQueueRef?.current?.addToast({ type: 'ERROR', description: (error as Error)?.message, timer: 2000 });
    }
  };

  const deleteAccount = async (code: string) => {
    if (code.length < 6) throw new Error('Code invalide');
    try {
      await usersService.deleteSkeziAccount(code);
      setIdentityConfirmationModal(false);
      toastQueueRef?.current?.addToast({ type: 'SUCCESS', description: 'Compte supprimé avec succès', timer: 5000 });
      logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toastQueueRef?.current?.addToast({ type: 'ERROR', description: (error as Error)?.message, timer: 2000 });
      throw error;
    }
  };

  return (
    <div className='flex flex-col items-start justify-start gap-8 rounded-2xl bg-white p-8 shadow-md'>
      <AuthSettingsItemHeader title='Supprimer mon compte' description='' noBorder />
      {!!userApps.length && (
        <p className='-mt-2.5 text-black'>La suppression de votre compte est définitive. En supprimant votre compte :</p>
      )}
      <div className='flex w-full flex-col gap-8 lg:grid lg:grid-cols-2'>
        {userApps.map(app => (
          <div
            key={app}
            className='border-gray-tertiary flex flex-col items-center justify-start gap-y-6 rounded-2xl border px-3 py-5 text-black sm:px-8'
          >
            <p className='text-xl font-bold'>{app}</p>
            {informationAboutDeletion
              .filter(
                item =>
                  item.app === app &&
                  (user?.skezia?.role === item.role ||
                    (user?.skezia?.role === 'USER' && user.skezia.hasParticipations) ||
                    user?.skezia?.role === 'ADMIN' ||
                    item.app !== 'SKEZIA'),
              )
              .map((item, i, array) => (
                <Fragment key={i}>
                  <div className='flex w-full items-start gap-2'>
                    <Close className='stroke-danger-default min-w-4 scale-80' />
                    <p className='text-sm font-medium'>{item.description}</p>
                  </div>
                  {i + 1 < array.length && <div className='border-b-gray-tertiary w-full border-b'></div>}
                </Fragment>
              ))}
          </div>
        ))}
      </div>
      <p className='text-black'>
        Pour toute question complémentaire, nous vous invitons à{' '}
        <span onClick={() => supportBtnRef?.current?.openChatbot()} className='text-green-dark cursor-pointer underline'>
          contacter le support
        </span>
        .
      </p>
      <div className='flex items-center justify-center gap-5 self-center *:cursor-pointer'>
        <BasicButton
          onClick={() => navigate('/dashboard/settings/privacy')}
          template='orangeOutline'
          fullRounded
          label='Annuler'
          className='mt-0! text-sm text-black!'
        />
        <BasicButton
          onClick={() => (user?.skezia?.hasKidsParticipants ? setKidsModalOpen(true) : setIdentityConfirmationModal(true))}
          template='orange'
          fullRounded
          label={window.innerWidth > 640 ? 'Supprimer mon compte' : 'Supprimer'}
          className='bg-danger-default! mt-0! text-sm'
        />
      </div>
      <YouHaveKidsModal open={kidsModalOpen} setOpen={setKidsModalOpen} />
      <IdentityConfirmationModal
        show={identityConfirmationModalOpen}
        setShow={setIdentityConfirmationModal}
        title='Une dernière vérification'
        description="Nous avons besoin de vérifier votre identité pour confirmer la suppression de votre compte. Veuillez saisir le code de vérification que nous vous avons envoyé par email (ou SMS si vous avez activé l'authentification à double facteur)."
        alert='Attention : Vous ne pourrez plus accéder à votre compte, ni récupérer vos données, ni réactiver votre profil après cette suppression définitive.'
        requestConfirmIdentity={requestConfirmIdentity}
        reSendCode={reSendCode}
        timeLimit={timeLimit}
        onConfirm={deleteAccount}
      />
    </div>
  );
};

export default DashboardDeleteAccount;
